/**
 * Hunter Evaluator - Predator-focused minimax
 *
 * Philosophy: SUPER focused on enemy CAPTURE
 * - Surrounding / forking / separating through DIVIDE AND CONQUER
 * - NOT worried about race or positional play
 * - Targeting easier pieces (LESS DEFENDED than others)
 * - Creating MULTI-DIRECTIONAL attacks on single points
 * - Picking out vulnerable/isolated targets
 */

import { GameState, IPiece, PieceType, Position, State } from "../../types";
import { EvaluationBreakdown } from "./SentinelEvaluator";
import { isRaceVictory, RACE_VICTORY_BONUS } from "./MinimaxUtils";

// Attack map: tracks how many attackers target each square
interface AttackMap {
  [key: string]: {
    // "row,col"
    attackerCount: number;
    attackers: { piece: IPiece; position: Position }[];
    defenderCount: number;
    defenders: { piece: IPiece; position: Position }[];
  };
}

export class HunterEvaluator {
  static lastBreakdown: EvaluationBreakdown | null = null;

  /**
   * Main evaluation function - evaluates from maximizing player's perspective
   * Positive = good for AI, Negative = bad for AI
   */
  static evaluate(
    gameState: GameState,
    isMaximizing: boolean,
    debug: boolean = false
  ): number {
    // Terminal state check (same as Sentinel)
    const terminalScore = this.checkTerminalState(gameState, isMaximizing);
    if (terminalScore !== null) return terminalScore;

    const aiIsLight = gameState.isLightTurn === isMaximizing;
    let score = 0;

    // 1. MINIMAL material counting (just enough to not blunder pieces)
    score += this.evaluateMaterial(gameState, aiIsLight) * 0.3;

    // 2. HEAVY emphasis on enemy vulnerability
    score += this.evaluateEnemyVulnerability(gameState, aiIsLight) * 3.0;

    // 3. FORK potential - threatening multiple pieces at once
    score += this.evaluateForkPotential(gameState, aiIsLight) * 2.5;

    // 4. ATTACK coordination - pieces working together
    score += this.evaluateAttackCoordination(gameState, aiIsLight) * 2.0;

    // 5. ENEMY isolation - separated pieces are easier to pick off
    score += this.evaluateEnemyIsolation(gameState, aiIsLight) * 1.5;

    // 6. SURROUNDING patterns - multi-directional pressure
    score += this.evaluateSurroundingPatterns(gameState, aiIsLight) * 2.0;

    // 7. Status effect scoring (entangled pieces are easy prey!)
    score += this.evaluateStatusEffects(gameState, aiIsLight) * 1.5;

    // 8. KING SAFETY — don't let our own king die (defensive minimum)
    score += this.evaluateKingSafety(gameState, aiIsLight) * 1.0;

    return score;
  }

  /**
   * Check for terminal game states
   */
  private static checkTerminalState(
    gameState: GameState,
    isMaximizing: boolean
  ): number | null {
    if (gameState.state === State.PostGame) {
      const aiIsLight = gameState.isLightTurn === isMaximizing;
      const winScore = 100000 + (isRaceVictory(gameState) ? RACE_VICTORY_BONUS : 0);
      if (gameState.lightWon === true) {
        return aiIsLight ? winScore : -winScore;
      }
      if (gameState.lightWon === false) {
        return aiIsLight ? -winScore : winScore;
      }
    }
    if (gameState.state === State.Draw || gameState.state === State.Terminated) {
      return 0;
    }
    return null;
  }

  /**
   * MINIMAL material evaluation - just enough to avoid blundering
   * Hunter cares about CAPTURING, not about piece values per se
   */
  private static evaluateMaterial(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          const isOurs = piece.isLight === aiIsLight;
          const multiplier = isOurs ? 1 : -1;

          // Basic piece value (flat 10 per piece)
          let pieceValue = 10;

          // King is critical (massive value to not lose)
          if (piece.isKing) {
            pieceValue += 1000;
          }

          // DOOM: Piece stacked with own Virus is effectively dead
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              pieceValue = -(pieceValue + 5);
            }
          }

          score += pieceValue * multiplier;
        }
      }
    }

    return score;
  }

  /**
   * CORE HUNTER MECHANIC: Evaluate how vulnerable enemy pieces are
   * Pieces with fewer defenders relative to attackers are HIGH VALUE targets
   */
  private static evaluateEnemyVulnerability(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    const attackMap = this.buildAttackMap(gameState, aiIsLight);
    let score = 0;

    // For each enemy piece, assess vulnerability
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === aiIsLight) continue; // Skip our pieces

          const key = `${row},${col}`;
          const squareData = attackMap[key];

          if (!squareData) continue;

          // Net vulnerability: more attackers than defenders = good for Hunter
          const netVulnerability =
            squareData.attackerCount - squareData.defenderCount;

          if (netVulnerability > 0) {
            // Bonus for vulnerable targets
            let vulnScore = netVulnerability * 15;

            // Extra bonus for king vulnerability
            if (piece.isKing) {
              vulnScore *= 5;
            }

            // Bonus if piece is entangled (cannot escape!)
            if (piece.isEntangled) {
              vulnScore *= 2;
            }

            // Bonus if piece is poisoned (will skip turn)
            if (piece.poisonedStatus?.isPoisoned) {
              vulnScore *= 1.5;
            }

            score += vulnScore;
          }
        }
      }
    }

    return score;
  }

  /**
   * Build attack map showing which squares we attack and which squares defend
   */
  private static buildAttackMap(
    gameState: GameState,
    aiIsLight: boolean
  ): AttackMap {
    const attackMap: AttackMap = {};

    // Initialize all squares
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const key = `${row},${col}`;
        attackMap[key] = {
          attackerCount: 0,
          attackers: [],
          defenderCount: 0,
          defenders: [],
        };
      }
    }

    // For each piece, determine what squares it attacks/defends
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          const position: Position = { row, col };

          // Skip entangled pieces (they can't attack)
          if (piece.isEntangled) continue;

          // Get attack pattern for this piece type
          const attackedSquares = this.getAttackSquares(
            piece,
            position,
            gameState
          );

          for (const targetPos of attackedSquares) {
            const key = `${targetPos.row},${targetPos.col}`;
            if (!attackMap[key]) continue;

            if (piece.isLight === aiIsLight) {
              // Our piece - adds to attack pressure
              attackMap[key].attackerCount++;
              attackMap[key].attackers.push({ piece, position });
            } else {
              // Enemy piece - adds to defense
              attackMap[key].defenderCount++;
              attackMap[key].defenders.push({ piece, position });
            }
          }
        }
      }
    }

    return attackMap;
  }

  /**
   * Get squares a piece can attack (based on piece type movement patterns)
   */
  private static getAttackSquares(
    piece: IPiece,
    position: Position,
    gameState: GameState
  ): Position[] {
    const squares: Position[] = [];

    // Different pieces have different attack patterns
    const directions = this.getAttackDirections(piece.type);
    const range = this.getAttackRange(piece.type, piece.isLight);

    for (const dir of directions) {
      for (let dist = 1; dist <= range; dist++) {
        const newRow = position.row + dir.row * dist;
        const newCol = position.col + dir.col * dist;

        if (newRow >= 0 && newRow < 8 && newCol >= 0 && newCol < 5) {
          squares.push({ row: newRow, col: newCol });

          // Stop if blocked by any piece (most pieces can't jump)
          // Exception: Crane can jump, Monkey can swing (simplified here)
          const blockingPieces = gameState.board[newRow][newCol].Pieces;
          if (
            blockingPieces.length > 0 &&
            piece.type !== PieceType.Crane &&
            piece.type !== PieceType.Monkey
          ) {
            break; // Blocked
          }
        }
      }
    }

    return squares;
  }

  /**
   * Get attack directions based on piece type
   */
  private static getAttackDirections(
    pieceType: PieceType
  ): { row: number; col: number }[] {
    const orthogonal = [
      { row: -1, col: 0 },
      { row: 1, col: 0 },
      { row: 0, col: -1 },
      { row: 0, col: 1 },
    ];
    const diagonal = [
      { row: -1, col: -1 },
      { row: -1, col: 1 },
      { row: 1, col: -1 },
      { row: 1, col: 1 },
    ];
    const all = [...orthogonal, ...diagonal];

    switch (pieceType) {
      case PieceType.Snake:
        return orthogonal;
      case PieceType.Frog:
        return diagonal;
      case PieceType.Crane:
        return orthogonal;
      case PieceType.Octopus:
        return orthogonal; // Captures orthogonally only
      case PieceType.Penguin:
        return orthogonal; // Slides orthogonally
      case PieceType.Rhino:
        // Rhino has forward focus - but can attack forward and back-diagonal
        return all;
      case PieceType.Wolf:
        // Wolf: 1 square any direction (or L-move when lone)
        return all;
      default:
        return all;
    }
  }

  /**
   * Get attack range based on piece type
   */
  private static getAttackRange(pieceType: PieceType, isLight: boolean): number {
    switch (pieceType) {
      case PieceType.Snake:
        return 2;
      case PieceType.Crane:
        return 2;
      case PieceType.Frog:
        return 2;
      case PieceType.Rhino:
        return 3; // Lane charge potential
      case PieceType.Penguin:
        return 7; // Slides full board
      case PieceType.Virus:
        return 2;
      default:
        return 1;
    }
  }

  /**
   * FORK POTENTIAL: Bonus for positions where we threaten multiple pieces
   */
  private static evaluateForkPotential(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    // For each of our pieces, count how many enemy pieces it threatens
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight !== aiIsLight) continue; // Only our pieces
          if (piece.isEntangled) continue;

          const position: Position = { row, col };
          const attackedSquares = this.getAttackSquares(
            piece,
            position,
            gameState
          );

          let threatenedEnemies = 0;
          let threatenedKing = false;

          for (const targetPos of attackedSquares) {
            const targetPieces =
              gameState.board[targetPos.row][targetPos.col].Pieces;
            for (const target of targetPieces) {
              if (target.isLight !== aiIsLight) {
                threatenedEnemies++;
                if (target.isKing) threatenedKing = true;
              }
            }
          }

          // FORK bonus: exponential reward for threatening multiple pieces
          if (threatenedEnemies >= 2) {
            let forkBonus = threatenedEnemies * threatenedEnemies * 10;
            if (threatenedKing) {
              forkBonus *= 2; // Fork involving king is extra valuable
            }
            score += forkBonus;
          }
        }
      }
    }

    return score;
  }

  /**
   * ATTACK COORDINATION: Reward pieces that work together
   * Multiple pieces targeting same square = coordinated attack
   */
  private static evaluateAttackCoordination(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    const attackMap = this.buildAttackMap(gameState, aiIsLight);
    let score = 0;

    // Bonus for squares we attack with multiple pieces
    for (const key in attackMap) {
      const squareData = attackMap[key];

      if (squareData.attackerCount >= 2) {
        // Coordinated attack bonus (quadratic scaling)
        const coordBonus = squareData.attackerCount * squareData.attackerCount * 5;

        // Extra bonus if enemy piece is on that square
        const [row, col] = key.split(",").map(Number);
        const targetPieces = gameState.board[row][col].Pieces;
        const hasEnemy = targetPieces.some((p) => p.isLight !== aiIsLight);

        if (hasEnemy) {
          score += coordBonus * 3; // Triple bonus for coordinated attacks on enemies
        } else {
          score += coordBonus; // Still good to control squares with multiple attackers
        }
      }
    }

    return score;
  }

  /**
   * ENEMY ISOLATION: Reward when enemy pieces are separated from allies
   * Isolated pieces are easier to pick off
   */
  private static evaluateEnemyIsolation(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    // Find all enemy pieces and calculate their isolation
    const enemyPieces: { piece: IPiece; position: Position }[] = [];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight !== aiIsLight) {
            enemyPieces.push({ piece, position: { row, col } });
          }
        }
      }
    }

    // For each enemy piece, check distance to nearest ally
    for (const { piece, position } of enemyPieces) {
      let nearestAllyDistance = Infinity;

      for (const { piece: other, position: otherPos } of enemyPieces) {
        if (piece.id === other.id) continue;

        const distance =
          Math.abs(position.row - otherPos.row) +
          Math.abs(position.col - otherPos.col);
        nearestAllyDistance = Math.min(nearestAllyDistance, distance);
      }

      // Isolated piece bonus (further from allies = more isolated)
      if (nearestAllyDistance >= 3) {
        let isolationBonus = nearestAllyDistance * 8;
        if (piece.isKing) {
          isolationBonus *= 3; // Isolated king is VERY good for Hunter
        }
        score += isolationBonus;
      }
    }

    return score;
  }

  /**
   * SURROUNDING PATTERNS: Reward when we attack from multiple directions
   */
  private static evaluateSurroundingPatterns(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    // For each enemy piece, count attack directions
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === aiIsLight) continue; // Only enemy pieces

          // Count how many directions we attack from
          const directions = new Set<string>();

          // Check all 8 surrounding squares
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (dr === 0 && dc === 0) continue;

              const checkRow = row + dr;
              const checkCol = col + dc;

              if (
                checkRow < 0 ||
                checkRow >= 8 ||
                checkCol < 0 ||
                checkCol >= 5
              )
                continue;

              const checkPieces = gameState.board[checkRow][checkCol].Pieces;
              for (const attacker of checkPieces) {
                if (attacker.isLight === aiIsLight && !attacker.isEntangled) {
                  // We have a piece adjacent to enemy
                  const dirKey = `${dr},${dc}`;
                  directions.add(dirKey);
                }
              }
            }
          }

          // Surrounding bonus (more directions = better containment)
          if (directions.size >= 2) {
            let surroundBonus = directions.size * directions.size * 8;
            if (piece.isKing) {
              surroundBonus *= 4; // Surrounded king is priority target
            }
            score += surroundBonus;
          }
        }
      }
    }

    return score;
  }

  /**
   * STATUS EFFECTS: Heavily reward targeting impaired pieces
   */
  private static evaluateStatusEffects(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          const isOurs = piece.isLight === aiIsLight;

          if (!isOurs) {
            // ENEMY impairments are GOOD for Hunter
            if (piece.isEntangled) {
              score += 25; // Entangled = sitting duck
            }
            if (piece.poisonedStatus?.isPoisoned) {
              score += 15; // Poisoned = will skip turn
            }
            if (piece.isFainted) {
              score += 20; // Fainted goat
            }
          } else {
            // OUR impairments are BAD
            if (piece.isEntangled) {
              score -= 15;
            }
            if (piece.poisonedStatus?.isPoisoned) {
              score -= 10;
            }
          }
        }
      }
    }

    return score;
  }

  /**
   * Get attack range for king safety calc (Penguin capped at 3).
   * Separate from the existing getAttackRange which returns 7 for Penguin.
   */
  private static getKingSafetyRange(pieceType: PieceType): number {
    switch (pieceType) {
      case PieceType.Snake: return 2;
      case PieceType.Crane: return 2;
      case PieceType.Frog: return 2;
      case PieceType.Rhino: return 3;
      case PieceType.Penguin: return 3;
      case PieceType.Virus: return 2;
      default: return 1;
    }
  }

  /**
   * OWN king safety only — defensive minimum to prevent king suicide.
   * Hunter's existing vulnerability/fork/surrounding evaluations handle enemy king pressure.
   */
  private static evaluateKingSafety(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    // Find our king
    let kingRow = -1, kingCol = -1;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        for (const piece of gameState.board[row][col].Pieces) {
          if (piece.isKing && piece.isLight === aiIsLight) {
            kingRow = row; kingCol = col;
          }
        }
      }
    }
    if (kingRow === -1) return -500; // No king = catastrophic

    let safety = 0;
    let friendlyAdjacent = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        for (const piece of gameState.board[row][col].Pieces) {
          if (piece.isLight === aiIsLight) {
            if (!piece.isKing) {
              const dist = Math.abs(row - kingRow) + Math.abs(col - kingCol);
              if (dist === 1) friendlyAdjacent++;
            }
            continue;
          }
          // Enemy piece — threat check
          if (piece.isEntangled) continue;
          const dist = Math.abs(row - kingRow) + Math.abs(col - kingCol);
          const attackRange = this.getKingSafetyRange(piece.type);
          if (dist <= attackRange) {
            safety -= (attackRange - dist + 1) * 12;
            if (piece.type === PieceType.Bear && dist <= 1) {
              safety -= 25;
            }
          }
        }
      }
    }

    // Lonely king rushing ahead
    const kingAdvancement = aiIsLight ? kingRow : (7 - kingRow);
    if (friendlyAdjacent === 0 && kingAdvancement >= 3) {
      safety -= 15;
    }

    // Edge column bonus
    if (kingCol === 0 || kingCol === 4) {
      safety += 3;
    }

    return safety;
  }

  /**
   * Quick evaluation for move ordering
   */
  static quickEvaluate(gameState: GameState, isMaximizing: boolean): number {
    const aiIsLight = gameState.isLightTurn === isMaximizing;
    let score = 0;

    // Quick material count
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          const multiplier = piece.isLight === aiIsLight ? 1 : -1;
          let pieceValue = 10;
          if (piece.isKing) pieceValue += 1000;
          // DOOM: Piece stacked with own Virus is effectively dead
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              pieceValue = -(pieceValue + 5);
            }
          }
          score += pieceValue * multiplier;
        }
      }
    }

    return score;
  }

  /**
   * Get absolute board value (for PV display - consistent Light perspective)
   */
  static getAbsoluteBoardValue(gameState: GameState): number {
    // Check for terminal states FIRST
    if (gameState.state === State.PostGame) {
      const winScore = 100000 + (isRaceVictory(gameState) ? RACE_VICTORY_BONUS : 0);
      if (gameState.lightWon === true) {
        return winScore;
      }
      if (gameState.lightWon === false) {
        return -winScore;
      }
    }
    if (gameState.state === State.Draw || gameState.state === State.Terminated) {
      return 0;
    }

    // Simplified absolute evaluation from Light's perspective
    let lightTotal = 0;
    let darkTotal = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          let pieceScore = 10;
          if (piece.isKing) pieceScore += 1000;

          // DOOM: Piece stacked with own Virus is effectively dead
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              pieceScore = -(pieceScore + 5);
            }
          }

          if (piece.isLight) {
            lightTotal += pieceScore;
          } else {
            darkTotal += pieceScore;
          }
        }
      }
    }

    return lightTotal - darkTotal;
  }

  /**
   * Get last breakdown for UI
   */
  static getLastBreakdown(): EvaluationBreakdown | null {
    return this.lastBreakdown;
  }
}
